<template>
  <el-button
    v-bind="{ ...$attrs, ...defaults }"
    :class="[
      weight && weights[weight],
      textSize && textSizes[textSize],
      roundStyle && rounds[roundStyle],
      {
        'line-height-normal': withIcon,
        'dark-bg': darkBackground,
        [textStyles[textStyle]]: textStyle,
      },
      classes,
      'app-button',
    ]"
  >
    <template
      v-for="(_, scopedSlotName) in $slots"
      #[scopedSlotName]="slotData"
    >
      <slot :name="scopedSlotName" v-bind="slotData" />
    </template>

    <template v-for="(_, slotName) in $slots" #[slotName]>
      <slot :name="slotName" />
    </template>
  </el-button>
</template>

<script setup lang="ts">
import { computed } from 'vue'

type TextWeight =
  | 'light'
  | 'regular'
  | 'medium'
  | 'semibold'
  | 'bold'
  | 'extrabold'
type TextStyle = 'uppercase' | 'lowercase' | 'capitalize' | 'normal-case'
type TextSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl'
type RoundType = 'md' | 'none' | 'lg' | 'xs' | 'full' | 'rounded'

const props = withDefaults(
  defineProps<{
    weight?: TextWeight
    withIcon?: boolean
    textStyle?: TextStyle
    bordered?: boolean
    textSize?: TextSize
    roundStyle?: RoundType
    onlyIcon?: boolean
    darkBackground?: boolean
  }>(),
  {
    withIcon: false,
    textStyle: 'uppercase',
    textSize: 'md',
    weight: 'bold',
    bordered: false,
    onlyIcon: false,
    darkBackground: false,
  }
)

const defaults = {}

const weights: Record<TextWeight, string> = {
  light: 'tw-font-light',
  regular: 'tw-font-normal',
  medium: 'tw-font-medium',
  semibold: 'tw-font-semibold',
  bold: 'tw-font-bold',
  extrabold: 'tw-font-extrabold',
}

const rounds: Record<RoundType, string> = {
  xs: 'tw-rounded-xs',
  md: 'tw-rounded-md',
  lg: 'tw-rounded-lg',
  none: 'tw-rounded-none',
  full: 'tw-rounded-full',
  rounded: 'tw-rounded',
}

const textStyles: Record<TextStyle, string> = {
  uppercase: 'tw-uppercase',
  lowercase: 'tw-lowercase',
  capitalize: 'tw-capitalize',
  'normal-case': 'tw-normal-case',
}

const textSizes: Record<TextSize, string> = {
  xs: 'tw-text-xs',
  sm: 'tw-text-sm',
  md: 'tw-text-base',
  lg: 'tw-text-lg',
  xl: 'tw-text-xl',
}

const classes = computed(() => ({
  'tw-border': props.bordered,
  'tw-h-full': props.onlyIcon,
}))
</script>

<style scoped lang="scss">
/* @description
  need to remove issue when zoom out page to 25-50% and button with icon has incorrect alignment with text
*/
.line-height-normal:deep(span) {
  line-height: normal;
  height: inherit;
}

/**
@description
  with metropolis font exists issue with button text alignment, so we need to decrease space in bottom for 1.5px
  TODO: remove it when font will be changed
*/
//.app-button {
//  padding: 8px 15px 6.5px;
//}

.dark-bg {
  &:hover {
    background-color: $dark-hover !important;
    box-shadow: 0 2px 5px $dark-hover;
  }
}
</style>
